/**
 * `/$org/spaces/$spaceId`: one space's overview, beside Home's sidebar, on
 * the theme's own page surface (as Home is), never a fixed dark one.
 *
 * The header names the space and its context, with who has access (faces)
 * and Settings at its end. Then, in order:
 *
 * 1. **Workflows**: the workflows filed in this space, for staff only (a
 *    client guest never sees workflows), with New workflow filing one here.
 * 2. **Chats**: the space's shared chats (who started each, its state, when
 *    it last moved); New chat opens one in Chat already filed here
 *    (`useStartSpaceChat`).
 * 3. **Files**: the space's documents with their versions (the Board first;
 *    it opens in place, `?doc=board`), then the files of its chats.
 * 4. **Policies and procedures**: the client organization's, each marked
 *    with what the client is shown. A space without a client shares none.
 * 5. **Change history**: one feed of what changed in the space.
 *
 * Settings (context, who has access) open in a sheet over the page
 * (`?settings=1`).
 *
 * @module components/spaces/SpacePage
 */
import { scopeThreadRef } from "@amoreg/client-runtime/environment";
import type { EnvironmentThreadShell } from "@amoreg/client-runtime/state/models";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  FileTextIcon,
  LayersIcon,
  MessageSquareIcon,
  MessageSquarePlusIcon,
  PencilLineIcon,
  PlusCircleIcon,
  Settings2Icon,
  ShieldCheckIcon,
  WorkflowIcon,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { useNowMinute } from "../../hooks/useNowMinute";
import { cn } from "../../lib/utils";
import { useStartSpaceChat } from "../../hooks/useStartSpaceChat";
import { useThreadCollaboration } from "../../state/collaboration";
import { useThreadShells } from "../../state/entities";
import { usePrimaryEnvironmentId } from "../../state/environments";
import { useOrgProfile } from "../../state/orgProfile";
import { useActiveOrg, useActiveOrgId, useOrgs } from "../../state/orgs";
import { orgUrlSegment } from "../../orgRouting";
import {
  CLIENT_ACCESS_DENY_ALL,
  useOrgSpaces,
  useSpace,
  useSpaceActivity,
  useSpaceDocuments,
  useSpaceGuests,
  type SpaceDetail,
} from "../../state/spaces";
import { buildThreadRouteParams } from "../../threadRoutes";
import { HomeSectionHeader } from "../home/HomeSectionHeader";
import { useWorkflows } from "../../state/workflows";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { NewWorkflowDialog } from "../workflows/WorkflowsPage";
import { RunStatusPill } from "../workflows/workflowParts";
import { SpaceGlyph } from "./SpaceGlyph";
import { compactAge, homeThreadState, selectOrgThreads, threadActivityMs } from "../home/homeLogic";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { Avatar, AvatarFallback, personInitials } from "../ui/avatar";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { Sheet, SheetDescription, SheetHeader, SheetPanel, SheetPopup, SheetTitle } from "../ui/sheet";
import { SidebarTrigger } from "../ui/sidebar";
import { Spinner } from "../ui/spinner";
import { Timeline, TimelineItem } from "../ui/timeline";
import { spaceActivityFeed, type SpaceActivityItem, type SpaceActivityTone } from "./spaceActivity";
import { SpaceDocumentView, SpaceFilesList, useSpaceChatFiles } from "./SpaceFiles";
import {
  OVERVIEW_CHATS,
  clientName,
  hostShortName,
  spaceChats,
  spaceDocumentRows,
  spaceMembers,
  spaceViewSearch,
  type SpacePageView,
  type VisibilityNames,
} from "./SpacePage.logic";
import { ProgramDocumentsSection, ProgramGate, useSpaceProgram, type SpaceProgramData } from "./SpaceProgram";
import { SpaceSettings } from "./SpaceSettings";

/* ------------------------------------------------------------------ */
/* Frame                                                               */
/* ------------------------------------------------------------------ */

/** The page surface: the theme's background, as Home's. */
const SURFACE = "flex h-full min-h-0 w-full min-w-0 flex-1 flex-col bg-background text-foreground";
const COLUMN = "mx-auto w-full max-w-4xl px-4 pb-24 pt-6 sm:px-8 md:pt-10";

function Notice({
  icon: Icon = LayersIcon,
  title,
  body,
  action,
}: {
  readonly icon?: typeof LayersIcon;
  readonly title: string;
  readonly body: string;
  readonly action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed border-border/70 px-6 py-12 text-center" data-space-notice="">
      <Icon className="size-5 text-muted-foreground/70" />
      <p className="mt-3 text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

function Frame({ children, wide = false }: { readonly children: ReactNode; readonly wide?: boolean }) {
  return (
    <div className={SURFACE} data-space-page="">
      <header className="flex h-11 shrink-0 items-center px-3 md:hidden">
        <SidebarTrigger aria-label="Open Home sidebar" />
      </header>
      <ScrollArea className="min-h-0 flex-1">
        {/* A sheet takes the page's width; everything else keeps the reading column. */}
        <div className={cn(COLUMN, wide && "max-w-7xl")}>{children}</div>
      </ScrollArea>
    </div>
  );
}

function BackToSpaces({ org }: { readonly org: string }) {
  return (
    <Link
      to="/$org/spaces"
      params={{ org }}
      className="mb-6 inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeftIcon aria-hidden className="size-3.5" />
      All spaces
    </Link>
  );
}

/** One overview section: Home's section header, then its body. */
function Section({
  id,
  title,
  count,
  aside,
  children,
}: {
  readonly id: string;
  readonly title: string;
  readonly count?: number | undefined;
  readonly aside?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <section aria-label={title} data-space-section={id}>
      <HomeSectionHeader title={title} count={count} aside={aside} />
      <div className="mt-2">{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Header                                                              */
/* ------------------------------------------------------------------ */

/** Who has access, as faces: the team first, then the client's guests as a count. */
function AccessFaces({
  detail,
  hostOrganizationId,
  onOpen,
}: {
  readonly detail: SpaceDetail;
  readonly hostOrganizationId: string;
  readonly onOpen: () => void;
}) {
  const clientOrganizationId = detail.space.client?.organizationId ?? null;
  const host = useOrgProfile(hostOrganizationId);
  const client = useOrgProfile(clientOrganizationId);
  const guests = useSpaceGuests(clientOrganizationId === null ? null : detail.space.id);
  const team =
    host.profile && (clientOrganizationId === null || client.profile)
      ? spaceMembers(host.profile.team, clientOrganizationId === null ? null : (client.profile?.team ?? []))
      : null;
  if (team === null) return null;
  const shown = team.slice(0, 4);
  const guestCount = guests.available ? guests.guests.length : 0;
  const label = `${team.length} on the team${guestCount > 0 ? `, ${guestCount} ${guestCount === 1 ? "guest" : "guests"}` : ""}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Who has access: ${label}`}
      title={label}
      className="flex cursor-pointer items-center gap-2 rounded-full py-0.5 pe-1 ps-0.5 outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring"
      data-space-access-faces=""
    >
      <span className="flex">
        {shown.map((member) => (
          <Avatar key={member.id} className="-ms-1 size-6 bg-foreground/10 text-[9px] leading-none ring-2 ring-background first:ms-0">
            <AvatarFallback>{personInitials(member)}</AvatarFallback>
          </Avatar>
        ))}
      </span>
      {team.length > shown.length || guestCount > 0 ? (
        <span className="text-xs tabular-nums text-muted-foreground">
          {team.length > shown.length ? `+${team.length - shown.length}` : ""}
          {guestCount > 0 ? `${team.length > shown.length ? " · " : ""}${guestCount} ${guestCount === 1 ? "guest" : "guests"}` : ""}
        </span>
      ) : null}
    </button>
  );
}

function SpaceHeader({
  detail,
  names,
  hostOrganizationId,
  onOpenSettings,
}: {
  readonly detail: SpaceDetail;
  readonly names: VisibilityNames;
  readonly hostOrganizationId: string;
  readonly onOpenSettings: () => void;
}) {
  const canManage = detail.access.kind === "staff" && detail.access.canManage;
  const context = detail.space.description?.trim() || null;
  return (
    <header className="flex flex-wrap items-start gap-x-5 gap-y-3" data-space-header="">
      <SpaceGlyph space={detail.space} className="mt-0.5 size-10 rounded-xl text-[15px]" />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-[22px] font-semibold leading-tight tracking-[-0.01em]">{detail.space.name}</h1>
          {detail.space.client ? (
            <span
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border/70 px-1.5 py-0.5 text-[11px] text-muted-foreground"
              title={`This space serves ${names.client}`}
              data-space-client=""
            >
              <ShieldCheckIcon aria-hidden className="size-3" />
              {names.client}
            </span>
          ) : null}
        </div>
        {context ? (
          <p className="mt-1 line-clamp-2 max-w-2xl whitespace-pre-line text-[13px] leading-relaxed text-muted-foreground" data-space-context-line="">
            {context}
          </p>
        ) : canManage ? (
          <button
            type="button"
            onClick={onOpenSettings}
            className="mt-1 cursor-pointer text-[13px] text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
          >
            Add context: what this space is for, so the team and the agent work from it
          </button>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <AccessFaces detail={detail} hostOrganizationId={hostOrganizationId} onOpen={onOpenSettings} />
        <Button size="sm" variant="outline" className="cursor-pointer rounded-full" onClick={onOpenSettings} data-space-settings-open="">
          <Settings2Icon />
          Settings
        </Button>
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* Chats                                                               */
/* ------------------------------------------------------------------ */

function ChatState({ thread, now }: { readonly thread: EnvironmentThreadShell; readonly now: number }) {
  const state = homeThreadState(thread);
  if (state === "working") return <Spinner className="size-3.5 text-muted-foreground" aria-label="Working" />;
  if (state === "waiting") {
    return <span role="img" aria-label="Waiting on you" className="size-1.5 shrink-0 rounded-full bg-primary" />;
  }
  return (
    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
      {compactAge(new Date(threadActivityMs(thread)).toISOString(), now)}
    </span>
  );
}

/** Who started the chat: its owner's face and first name. */
function ChatOwner({ threadId }: { readonly threadId: string }) {
  const { owner } = useThreadCollaboration(threadId);
  if (owner === null) return <span className="w-24 shrink-0 max-sm:hidden" />;
  const name = owner.firstName?.trim() || owner.email.split("@")[0] || owner.email;
  return (
    <span className="flex w-24 shrink-0 items-center gap-1.5 max-sm:hidden" title={owner.email}>
      <Avatar className="size-5 bg-foreground/10 text-[8px]">
        <AvatarFallback>{personInitials({ name: [owner.firstName, owner.lastName].filter(Boolean).join(" ") || null, email: owner.email })}</AvatarFallback>
      </Avatar>
      <span className="truncate text-xs text-muted-foreground">{name}</span>
    </span>
  );
}

/**
 * The space's workflows, staff only: the page renders this for staff and the
 * server refuses guests outright. New workflow files one in this space.
 */
function WorkflowsSection({
  organizationId,
  space,
}: {
  readonly organizationId: string;
  readonly space: SpaceDetail["space"];
}) {
  const { read } = useWorkflows(organizationId, space.id);
  const [creating, setCreating] = useState(false);
  const org = orgUrlSegment(organizationId);
  const workflows = read?.kind === "ready" ? read.value : [];
  return (
    <Section
      id="workflows"
      title="Workflows"
      count={read?.kind === "ready" ? workflows.length : undefined}
      aside={
        <Button size="xs" variant="ghost" className="cursor-pointer" onClick={() => setCreating(true)} data-space-new-workflow="">
          <PlusCircleIcon />
          New workflow
        </Button>
      }
    >
      {read === null ? (
        <ListSkeleton rows={2} />
      ) : read.kind !== "ready" ? (
        <p className="py-2 text-[13px] text-muted-foreground">Workflows could not be loaded. Reload the page to try again.</p>
      ) : workflows.length === 0 ? (
        <p className="py-2 text-[13px] text-muted-foreground" data-space-workflows="">
          No workflows yet. Make one for work you repeat for {space.name}.
        </p>
      ) : (
        <ul className="-mx-2" data-space-workflows="">
          {workflows.map((workflow) => (
            <li key={workflow.id}>
              <Link
                to="/$org/workflows/$workflowId"
                params={{ org, workflowId: workflow.id }}
                className="flex min-h-10 items-center gap-3 rounded-lg px-2 py-1.5 text-[13px] outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring"
                data-space-workflow={workflow.id}
              >
                <WorkflowIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-foreground">{workflow.name}</span>
                {workflow.lastRun ? (
                  <>
                    <RunStatusPill status={workflow.lastRun.status} />
                    <span className="w-14 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                      {formatRelativeTimeLabel(workflow.lastRun.createdAt)}
                    </span>
                  </>
                ) : (
                  <span className="shrink-0 text-xs text-muted-foreground">Never run</span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
      <NewWorkflowDialog organizationId={organizationId} spaceId={space.id} open={creating} onOpenChange={setCreating} />
    </Section>
  );
}

function ChatsSection({
  space,
  chats,
  now,
  onNewChat,
}: {
  readonly space: SpaceDetail["space"];
  readonly chats: ReadonlyArray<EnvironmentThreadShell>;
  readonly now: number;
  readonly onNewChat: (() => void) | null;
}) {
  const [all, setAll] = useState(false);
  const shown = all ? chats : chats.slice(0, OVERVIEW_CHATS);
  return (
    <Section
      id="chats"
      title="Chats"
      count={chats.length}
      aside={
        <Button size="xs" variant="ghost" className="cursor-pointer" disabled={onNewChat === null} onClick={() => onNewChat?.()} data-space-new-chat="">
          <MessageSquarePlusIcon />
          New chat in {space.name}
        </Button>
      }
    >
      {chats.length === 0 ? (
        <p className="py-2 text-[13px] text-muted-foreground">
          No chats yet. Start one, or file a chat here from its composer.
        </p>
      ) : (
        <>
          <ul className="-mx-2" data-space-chats="">
            {shown.map((thread, index) => (
              <li key={thread.id}>
                <Link
                  to="/$org/$threadId"
                  params={buildThreadRouteParams(scopeThreadRef(thread.environmentId, thread.id), thread.projectId)}
                  className="flex min-h-10 items-center gap-3 rounded-lg px-2 py-1.5 text-[13px] outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring"
                  data-space-chat={thread.id}
                >
                  <MessageSquareIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-foreground">{thread.title}</span>
                  {/* Faces for the rows a person reads first; each costs one lookup. */}
                  {index < OVERVIEW_CHATS ? <ChatOwner threadId={thread.id} /> : null}
                  <span className="flex w-10 shrink-0 justify-end">
                    <ChatState thread={thread} now={now} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {chats.length > OVERVIEW_CHATS ? (
            <button
              type="button"
              onClick={() => setAll((open) => !open)}
              className="mt-1 cursor-pointer text-xs text-muted-foreground transition-colors hover:text-foreground"
              data-space-chats-all=""
            >
              {all ? "Show fewer" : `See all ${chats.length} chats`}
            </button>
          ) : null}
        </>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Policies and procedures                                             */
/* ------------------------------------------------------------------ */

interface ClientContext {
  readonly program: SpaceProgramData;
  readonly segment: string;
  readonly names: VisibilityNames;
}

function PoliciesSection({
  detail,
  client,
  now,
}: {
  readonly detail: SpaceDetail;
  readonly client: ClientContext | null;
  readonly now: number;
}) {
  if (client === null) {
    return (
      <Section id="pnps" title="Policies and procedures">
        <p className="py-2 text-[13px] text-muted-foreground">
          A space shares policies and procedures once it serves a client organization. Link one in Settings, under
          Who has access.
        </p>
      </Section>
    );
  }
  const access = detail.clientAccess ?? CLIENT_ACCESS_DENY_ALL;
  return (
    <div data-space-section="pnps">
      <ProgramGate program={client.program} clientName={client.names.client}>
        <ProgramDocumentsSection
          program={client.program}
          access={access}
          names={client.names}
          clientSegment={client.segment}
          now={now}
          limit={6}
        />
      </ProgramGate>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Change history                                                      */
/* ------------------------------------------------------------------ */

const TONE_ICON: Readonly<Record<SpaceActivityTone, ReactNode>> = {
  created: <PlusCircleIcon className="size-3.5" />,
  chat: <MessageSquareIcon className="size-3.5" />,
  file: <FileTextIcon className="size-3.5" />,
  document: <PencilLineIcon className="size-3.5" />,
  access: <ShieldCheckIcon className="size-3.5" />,
  settings: <Settings2Icon className="size-3.5" />,
};

function ActivityRow({ item, now }: { readonly item: SpaceActivityItem; readonly now: number }) {
  const age = compactAge(item.at, now);
  return (
    <p className="text-xs leading-5 text-muted-foreground" data-space-activity={item.tone}>
      {item.actor ? <span className="font-medium text-foreground/80">{item.actor} </span> : null}
      {item.text}
      <time dateTime={item.at} title={new Date(item.at).toLocaleString("en-GB")} className="text-muted-foreground/80">
        {" "}
        · {age === "now" ? "just now" : `${age} ago`}
      </time>
    </p>
  );
}

function HistorySection({ items, now, loading }: { readonly items: ReadonlyArray<SpaceActivityItem>; readonly now: number; readonly loading: boolean }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, 12);
  return (
    <Section id="history" title="Change history">
      {loading ? (
        <ListSkeleton rows={3} />
      ) : (
        <>
          <Timeline aria-label="Change history" className="mt-3" data-space-history="">
            {shown.map((item) => (
              <TimelineItem key={item.id} compact indicator={<span className="text-muted-foreground">{TONE_ICON[item.tone]}</span>}>
                <ActivityRow item={item} now={now} />
              </TimelineItem>
            ))}
          </Timeline>
          {items.length > 12 ? (
            <button
              type="button"
              onClick={() => setAll((open) => !open)}
              className="cursor-pointer text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              {all ? "Show less" : `Show all ${items.length} changes`}
            </button>
          ) : null}
        </>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function SpacePage({ spaceId, view }: { readonly spaceId: string; readonly view: SpacePageView }) {
  const orgId = useActiveOrgId();
  const activeOrg = useActiveOrg();
  const space = useSpace(spaceId, orgId);
  const orgSpaces = useOrgSpaces(orgId);
  const environmentId = usePrimaryEnvironmentId();
  const threads = useThreadShells();
  const nowMinute = useNowMinute();
  const now = Date.parse(`${nowMinute}:00.000Z`) || Date.now();
  const navigate = useNavigate();
  const startChat = useStartSpaceChat();
  const newChat = startChat === null ? null : () => void startChat(spaceId);
  const documents = useSpaceDocuments(spaceId);
  const documentRows = useMemo(() => spaceDocumentRows(documents.items), [documents.items]);
  const activity = useSpaceActivity(spaceId);
  const { orgs } = useOrgs();

  const detail = space.detail;
  const clientOrgId = detail?.space.client?.organizationId ?? null;
  const program = useSpaceProgram(clientOrgId);
  const chats = useMemo(
    () => spaceChats(selectOrgThreads(threads, { orgId, environmentId }), spaceId, orgSpaces.spaceIdByThreadId),
    [environmentId, orgId, orgSpaces.spaceIdByThreadId, spaceId, threads],
  );
  const spaceThreadIds = useMemo(() => new Set<string>(chats.map((thread) => thread.id)), [chats]);
  const chatFiles = useSpaceChatFiles(environmentId, orgId, spaceThreadIds);
  const history = useMemo(
    () =>
      detail
        ? spaceActivityFeed({
            space: detail.space,
            serverEvents: activity.available ? activity.items : null,
            chats,
            chatFiles: chatFiles.files,
            limit: 100,
            orgName: (id) => orgs.find((candidate) => candidate.id === id)?.name ?? null,
          })
        : [],
    [activity.available, activity.items, chatFiles.files, chats, detail, orgs],
  );
  const org = orgId ? orgUrlSegment(orgId) : null;
  const hostName = activeOrg?.name ?? null;
  const names: VisibilityNames | null = detail
    ? { host: hostShortName(hostName), client: clientName(detail.space) }
    : null;
  const client: ClientContext | null =
    clientOrgId !== null && names !== null ? { program, segment: orgUrlSegment(clientOrgId), names } : null;

  // The space is the host's: under another organization's URL it is not found.
  const inOtherOrg =
    detail !== null && orgSpaces.available && !orgSpaces.isPending && !orgSpaces.failed &&
    !orgSpaces.spaces.some((candidate) => candidate.id === spaceId);

  // Until the URL organization's list arrives, the space cannot be tied to it:
  // nothing renders, so no action can run under the wrong organization.
  if (org === null || orgId === null || space.isPending || (orgSpaces.available && orgSpaces.isPending)) {
    return (
      <Frame>
        <ListSkeleton rows={5} />
      </Frame>
    );
  }
  if (space.notFound || inOtherOrg) {
    return (
      <Frame>
        <BackToSpaces org={org} />
        <Notice title="Space not found" body="It does not exist, or it is not yours to see." />
      </Frame>
    );
  }
  if (space.failed || detail === null || names === null) {
    return (
      <Frame>
        <BackToSpaces org={org} />
        <Notice
          title="The space could not be loaded"
          body="The workspace server did not answer. Try again in a moment."
          action={
            <Button size="sm" variant="outline" className="rounded-full" onClick={space.refresh}>
              Try again
            </Button>
          }
        />
      </Frame>
    );
  }

  const go = (next: SpacePageView) =>
    void navigate({ to: "/$org/spaces/$spaceId", params: { org, spaceId }, search: spaceViewSearch(next) });
  const openSettings = () => go({ ...view, settings: true });

  const openRow = view.doc === null ? null : (documentRows.find((row) => row.docId === view.doc) ?? null);
  return (
    <Frame wide={view.doc?.startsWith("sheet:") ?? false}>
      {view.doc !== null ? (
        <SpaceDocumentView
          spaceId={spaceId}
          doc={view.doc}
          row={openRow}
          canEdit={detail.access.kind === "staff"}
          now={now}
          onClose={() => go({ ...view, doc: null })}
        />
      ) : (
        <div className="space-y-10" data-space-overview="">
          <SpaceHeader detail={detail} names={names} hostOrganizationId={orgId} onOpenSettings={openSettings} />
          {/* Staff only: a client guest never sees workflows. */}
          {detail.access.kind === "staff" ? <WorkflowsSection organizationId={orgId} space={detail.space} /> : null}
          <ChatsSection space={detail.space} chats={chats} now={now} onNewChat={newChat} />
          <Section id="files" title="Files">
            <SpaceFilesList
              spaceId={spaceId}
              canUpload={detail.access.kind === "staff"}
              documents={documents}
              chatFiles={chatFiles}
              environmentId={environmentId}
              hostOrganizationId={orgId}
              now={now}
              onOpenDoc={(doc) => go({ doc, settings: false })}
              limit={5}
            />
          </Section>
          <PoliciesSection detail={detail} client={client} now={now} />
          <HistorySection items={history} now={now} loading={activity.isPending} />
        </div>
      )}
      <Sheet open={view.settings} onOpenChange={(open) => (open ? openSettings() : go({ ...view, settings: false }))}>
        <SheetPopup side="right" className="max-w-xl" data-space-settings-sheet="">
          <SheetHeader>
            <SheetTitle>{detail.space.name} settings</SheetTitle>
            <SheetDescription>The space's context, and who has access.</SheetDescription>
          </SheetHeader>
          <SheetPanel>
            <SpaceSettings detail={detail} view={space} hostShort={names.host} hostOrganizationId={orgId} />
          </SheetPanel>
        </SheetPopup>
      </Sheet>
    </Frame>
  );
}
